<script setup lang="ts">
import { ref, computed } from "vue";
import {
  formatTimeFromSeconds,
  convertSecondsFromStringTime,
  dateToNumber,
  formatTime,
  formatDateTime,
} from "@/services/functions";
import DeliveryPlanType from "@/types/DeliveryPlan"
import AlertSettingType from "@/types/AlertSetting"
import i18n from '@/lang/i18n';

const props = defineProps<{
  rowSpan: number;
  beforeTrip: DeliveryPlanType;
  trip: DeliveryPlanType;
  afterTrip: DeliveryPlanType;
  modelValue: DeliveryPlanType;
  maxWorkingTime: number;
  alertSetting: AlertSettingType;
  showAlert: (message: string) => boolean;
  setValidationResult: (key: string, value: boolean) => null;
  leavingDiffTime: number
}>();
const emit = defineEmits(["update:modelValue"]);

const values = ref(props.modelValue);
const startAtIsValid = ref(true);
const endAtIsValid = ref(true);
let _origin: DeliveryPlanType;
const MID_NIGHT_START_TIME = "22:00:00";
const MID_NIGHT_END_TIME = "05:00:00";

const origin = computed(() => {
  if (_origin) return _origin;
  _origin = props.trip;
  return _origin;
});

const handleChangeStartAt = (event) => {
  const date = origin.value.csv_start_working_time.split(" ")[0];

  let time = event.target.value;
  const times = time.split(":");
  if (time.length == 3) time = times[0] + ":" + times[1];
  values.value.csv_start_working_time = date + " " + time;

  validateStartAt();
  emit("update:modelValue", values.value);
};

const handleChangeEndAt = (event) => {
  let time = event.target.value;
  const times = time.split(":");
  if (time.length == 3) time = times[0] + ":" + times[1];
  values.value.csv_end_working_time = formatDateTime(
    new Date(event.target.value)
  );

  validateEndAt();
  emit("update:modelValue", values.value);
};

const createMidnightWorkingTimeRangeArray = (startDateTime: string, endDateTime: string) => {
  const [startSplitedDate, startSplitedTime] = startDateTime.split(" ");
  const [endSplitedDate, endSplitedTime] = (endDateTime ?? "").split(" ");

  let startMidnightWorkingTime: string | null = null;
  if (startSplitedTime < MID_NIGHT_END_TIME) {
    startMidnightWorkingTime = `${startSplitedDate} ${startSplitedTime}`;
  }

  if (
    !startMidnightWorkingTime &&
    startSplitedDate == endSplitedDate &&
    endSplitedTime <= MID_NIGHT_START_TIME
  ) {
    return [];
  }

  if (!startMidnightWorkingTime) {
    const startTime =
      MID_NIGHT_START_TIME < startSplitedTime
        ? startSplitedTime
        : MID_NIGHT_START_TIME;
    startMidnightWorkingTime = `${startSplitedDate} ${startTime}`;
  }

  let endMidnightWorkingTime = endDateTime;
  if (MID_NIGHT_END_TIME < endSplitedTime && endSplitedTime <= MID_NIGHT_START_TIME) {
    endMidnightWorkingTime = `${endSplitedDate} ${MID_NIGHT_END_TIME}`;
  }

  const rangeArray = [];
  while (startMidnightWorkingTime < endMidnightWorkingTime) {
    const range = { start: startMidnightWorkingTime, end: "" };
    let [rangeStartDate, rangeStartTime]: string[] = startMidnightWorkingTime.split(" ");
    let rangeEndTime: string;

    if (rangeStartTime >= MID_NIGHT_START_TIME) {
      const DateInst = new Date(startMidnightWorkingTime);
      DateInst.setDate(DateInst.getDate() + 1);
      rangeStartDate = `${DateInst.getFullYear()}/${month(DateInst.getMonth())}/${DateInst.getDate().toString().padStart(2, '0')}`;
    }

    rangeEndTime = `${rangeStartDate} ${MID_NIGHT_END_TIME}`;
    if (rangeEndTime > endMidnightWorkingTime) rangeEndTime = endMidnightWorkingTime;

    range.end = rangeEndTime;
    rangeArray.push(range);
    startMidnightWorkingTime = `${rangeStartDate} ${MID_NIGHT_START_TIME}`;
  }

  return rangeArray;
};

const month = (index: number) => {
  return [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
  ][index];
};

const calcDiffSecondsFromStartToEnd = (range: Array<any>) => {
  let diffSeconds = 0;
  for (let i = 0; i < range.length; i++) {
    diffSeconds +=
      (new Date(range[i].end).getTime() - new Date(range[i].start).getTime()) /
      1000;
  }
  return diffSeconds;
};

// validation
const validateStartAt = () => {
  const target = new Date(values.value.csv_start_working_time);
  let valid = target <= new Date(origin.value.csv_leaving);
  if (props.beforeTrip) {
    valid = valid && target > new Date(props.beforeTrip.csv_end_working_time);
  }
  startAtIsValid.value = valid;
  if (!valid) props.showAlert(invalidStartAtMessage());

  props.setValidationResult(
    origin.value.delivery_plan_id + '-' + origin.value.sub_id,
    valid
  );
  return startAtIsValid.value;
};

function validateEndAt() {
  const target = new Date(values.value.csv_end_working_time);
  let valid = target >= new Date(origin.value.csv_returning);
  if (props.afterTrip) {
    valid = valid && target < new Date(props.afterTrip.csv_start_working_time);
  }
  endAtIsValid.value = valid;
  if (!valid) props.showAlert(invalidEndAtMessage());

  props.setValidationResult(
    origin.value.delivery_plan_id + '-' + origin.value.sub_id,
    valid
  );
  return endAtIsValid.value;
}

const invalidStartAtMessage = () => {
  let beforeEnd = props.beforeTrip?.csv_end_working_time;
  const beforeDate = dateToNumber(
    new Date(props.beforeTrip?.csv_start_working_time)
  );
  const tripDate = dateToNumber(new Date(values.value.csv_start_working_time));
  if (tripDate - beforeDate != 0) beforeEnd = null;

  return makeAlertMessage(
    values.value.csv_start_working_time,
    beforeEnd,
    origin.value.csv_leaving,
    "start"
  );
};

const invalidEndAtMessage = () => {
  return makeAlertMessage(
    values.value.csv_end_working_time,
    origin.value.csv_returning,
    props.afterTrip?.csv_start_working_time,
    "end"
  );
};

const makeAlertMessage = (
  current: string,
  before: string | null,
  after: string | null,
  position: string
) => {
  let beforeDateTime: Date | null = null;
  let afterDateTime: Date | null = null;
  const currentDateTime = new Date(current);
  if (before) beforeDateTime = new Date(before);
  if (after) afterDateTime = new Date(after);
  const validBefore = !!beforeDateTime?.getTime();
  const validAfter = !!afterDateTime?.getTime();

  if (position == "start") {
    if (validBefore)
      beforeDateTime = new Date(
        beforeDateTime.setMinutes(beforeDateTime.getMinutes() + 1)
      );
  } else {
    if (validAfter)
      afterDateTime = new Date(
        afterDateTime.setMinutes(afterDateTime.getMinutes() - 1)
      );
  }

  let beforeString: string | null = null;
  let afterString: string | null = null;
  if (validBefore) beforeString = makeFormatedTime(beforeDateTime);
  if (validAfter) afterString = makeFormatedTime(afterDateTime);
  if (position == "end") {
    if (validBefore)
      beforeString = makeFormatedDate(beforeDateTime) + " " + beforeString;
    if (validAfter)
      afterString = makeFormatedDate(afterDateTime) + " " + afterString;
  }

  let message = `${beforeString} ${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.between')} ${afterString} ${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.inputbetweenDate')}`;
  if (position == "start" && !validBefore) {
    message = `${afterString} ${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.inputBeforeTime')}`;
  }
  if (position == "end" && !validAfter) {
    return `${beforeString} ${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.inputAfterDate')}`;
  }
  return message;
};

const makeFormatedTime = (date: Date) => {
  const h = date.getHours();
  const mm = date.getMinutes();
  return `${(h <= 9 ? "0" + h : h)}${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.hour')}${(mm <= 9 ? "0" + mm : mm)}${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.minute')}`;
};

const makeFormatedDate = (date: Date) => {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear().toString();
  return (
    `${y}${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.year')}${(m <= 9 ? "0" + m : m)}${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.month')}${(d <= 9 ? "0" + d : d)}${i18n.global.t('pages.monthlyReportWorkingTimesSetting.trip.day')}`
  );
};

// style
const fromReturnToNextLeavingStyle = computed(() => {
  if (!nextTimeByTrip.value) return "";

  return nextTimeByTrip.value < 8 * 60 * 60 ? "color: red;" : "";
});

const startAtColor = computed(() => {
  return !startAtIsValid.value ? "background-color: pink;" : "";
});

const endAtColor = computed(() => {
  return !endAtIsValid.value ? "background-color: pink;" : "";
});

// 明細、小計の時間はアラートLv3をチェックしない
const restraintTimeStyle = computed(() => {
  if (!totalByTrip.value) return "color: black;";

  const restraintTimeSeconds = totalByTrip.value;

  const alertLv2TimeSeconds = convertSecondsFromStringTime(
    props.alertSetting?.alertLv2Time
  );
  if (
    props.alertSetting?.alertLv2Time &&
    restraintTimeSeconds > alertLv2TimeSeconds
  ) {
    return "color: " + props.alertSetting?.alertLv2Color;
  }

  const alertLv1TimeSeconds = convertSecondsFromStringTime(
    props.alertSetting?.alertLv1Time
  );
  if (
    props.alertSetting?.alertLv1Time &&
    restraintTimeSeconds > alertLv1TimeSeconds
  ) {
    return "color: " + props.alertSetting?.alertLv1Color;
  }

  return "color: black;";
});

const updateValue = (key: string, value: number) => {
  values.value[key] = value;
  emit("update::modelValue", values.value);
};

const originStartTimeDiff = computed(() => {
  return (new Date(origin.value.csv_start_working_time) -
      new Date(values.value.csv_start_working_time)) /
    1000;
})

const originEndTimeDiff = computed(() => {
  return (new Date(values.value.csv_end_working_time) -
      new Date(origin.value.csv_end_working_time)) /
    1000;
})

// computed
const nextTimeByTrip = computed(() => {
  if (!props.afterTrip) {
    if (!values.value.next_time_by_trip) return null;
    const value = origin.value.next_time_by_trip - originEndTimeDiff.value;
    updateValue("next_time_by_trip", value);
    return value;
  }
  const value =
    (new Date(props.afterTrip.csv_start_working_time) -
    new Date(values.value.csv_end_working_time)) /
    1000;
  updateValue("next_time_by_trip", value);
  return value;
});

const othersTime = computed(() => {
  const beforeDiff = originStartTimeDiff.value
  const afterDiff = originEndTimeDiff.value;
  let diff = origin.value.others_time + beforeDiff + afterDiff;
  if (diff < 0) diff = 0;
  updateValue("others_time", diff);
  return diff;
});

const totalByTrip = computed(() => {
  const value = workingTime.value + values.value.break + props.leavingDiffTime;
  updateValue("total_by_trip", value);
  return value;
});

const workingTime = computed(() => {
  const value =
    values.value.driving +
    values.value.loading_time +
    values.value.unloading_time +
    values.value.shipping_time +
    values.value.wait_time +
    (othersTime.value ?? 0);
  updateValue("working_time", value);
  return value;
});

const overTime = computed(() => {
  let diff = workingTime.value
  if (props.maxWorkingTime?.seconds) {
    diff = (workingTime.value ?? 0) - props.maxWorkingTime.seconds;
  }
  if (diff < 0) diff = 0;
  updateValue("over_time", diff);
  return diff;
});

// show formatted time
const startWorkingTime = computed(() => {
  return formatTime(new Date(values.value.csv_start_working_time));
});

const endWorkingTime = computed(() => {
  return formatDateTime(new Date(values.value.csv_end_working_time));
});

const formattedNextTimeByTrip = computed(() => {
  if (nextTimeByTrip.value == null) return "";
  return formatTimeFromSeconds(nextTimeByTrip.value);
});

const formattedMileage = computed(() => {
  if (values.value?.total_mileage == 0) return "";
  return values.value.total_mileage;
});

const formattedTotalDriving = computed(() => {
  if (values.value?.total_driving == null) return "";
  return formatTimeFromSeconds(values.value?.total_driving);
});

const formattedDriving = computed(() => {
  if (values.value?.driving == null) return "";
  return formatTimeFromSeconds(values.value?.driving);
});

const formattedBreak = computed(() => {
  if (values.value?.break == null) return "";
  return formatTimeFromSeconds(values.value?.break);
});

const formattedLoadingTime = computed(() => {
  if (values.value?.loading_time == null) return "";
  return formatTimeFromSeconds(values.value?.loading_time);
});

const formattedUnloadingTime = computed(() => {
  if (values.value?.unloading_time == null) return "";
  return formatTimeFromSeconds(values.value?.unloading_time);
});

const formattedShippingTime = computed(() => {
  if (values.value?.shipping_time == null) return "";
  return formatTimeFromSeconds(values.value?.shipping_time);
});

const formattedWaitTime = computed(() => {
  if (values.value?.wait_time == null) return "";
  return formatTimeFromSeconds(values.value?.wait_time);
});

const formattedOthersTime = computed(() => {
  if (othersTime.value == null) return "";
  return formatTimeFromSeconds(othersTime.value);
});

const formattedTotalByTrip = computed(() => {
  if (totalByTrip.value == null) return "";
  return formatTimeFromSeconds(totalByTrip.value);
});

const formattedWorkingTime = computed(() => {
  if (workingTime.value == null) return "";
  return formatTimeFromSeconds(workingTime.value);
});

const formattedOverTime = computed(() => {
  if (overTime.value == null) return "";
  return formatTimeFromSeconds(overTime.value);
});

const overNightWorkTime = computed(() => {
  const originStartTimeSeconds = new Date(origin.value.csv_start_working_time).getTime()
  const startTimeSeconds = new Date(values.value.csv_start_working_time).getTime()
  const originEndTimeSeconds = new Date(origin.value.csv_end_working_time).getTime()
  const endTimeSeconds = new Date(values.value.csv_end_working_time).getTime()
  
  let overNightTime = origin.value.over_night_time
  if (startTimeSeconds != originStartTimeSeconds) {
    overNightTime += startTimeSeconds < originStartTimeSeconds
      ? createDiffSeconds.value(values.value.csv_start_working_time.replace(/-/g, "/"), origin.value.csv_start_working_time)
      : -createDiffSeconds.value(origin.value.csv_start_working_time, values.value.csv_start_working_time.replace(/-/g, "/"))
  }
  if (endTimeSeconds != originEndTimeSeconds) {
    overNightTime += endTimeSeconds < originEndTimeSeconds
      ? -createDiffSeconds.value(values.value.csv_end_working_time.replace(/-/g, "/"), origin.value.csv_end_working_time)
      : createDiffSeconds.value(origin.value.csv_end_working_time, values.value.csv_end_working_time.replace(/-/g, "/"))
  }
  updateValue("over_night_time", overNightTime);
  return overNightTime
});

const createDiffSeconds = computed(() => (start: string, end: string) => {
  return calcDiffSecondsFromStartToEnd(createMidnightWorkingTimeRangeArray(start, end));
})
</script>

<template>
  <td :rowspan="rowSpan" :style="startAtColor">
    <input
      type="time"
      class="start-work-time"
      :class="`working-time-color-${trip?.working_time_color?.start}`"
      :value="startWorkingTime"
      @change="handleChangeStartAt"
    />
  </td>
  <td :rowspan="rowSpan" :style="endAtColor">
    <input
      type="datetime-local"
      :value="endWorkingTime"
      class="end-work-time"
      :class="`working-time-color-${trip?.working_time_color?.end}`"
      @change="handleChangeEndAt"
    />
  </td>
  <td :rowspan="rowSpan">{{ formattedTotalDriving }}</td>

  <td :rowspan="rowSpan" :style="fromReturnToNextLeavingStyle">
    {{ formattedNextTimeByTrip }}
  </td>
  <td :rowspan="rowSpan">{{ formattedMileage }}</td>
  <td :rowspan="rowSpan">{{ formattedDriving }}</td>
  <td :rowspan="rowSpan">{{ formattedBreak }}</td>
  <td :rowspan="rowSpan">{{ formattedLoadingTime }}</td>
  <td :rowspan="rowSpan">{{ formattedUnloadingTime }}</td>
  <td :rowspan="rowSpan">{{ formattedShippingTime }}</td>
  <td :rowspan="rowSpan">{{ formattedWaitTime }}</td>
  <td :rowspan="rowSpan">{{ formattedOthersTime }}</td>
  <td :rowspan="rowSpan" :style="restraintTimeStyle">
    {{ formattedTotalByTrip }}
  </td>
  <td :rowspan="rowSpan">{{ formattedWorkingTime }}</td>
  <td :rowspan="rowSpan">{{ formatTimeFromSeconds(props.leavingDiffTime) }}</td>
  <td :rowspan="rowSpan">
    <p v-for="(restData, rest_i) in props.trip.rest_data" :key="rest_i">
      {{ restData.start }}
    </p>
  </td>
  <td :rowspan="rowSpan">
    <p v-for="(restData, rest_i) in props.trip.rest_data" :key="rest_i">
      {{ restData.end }}
    </p>
  </td>
  <td :rowspan="rowSpan">
    <p v-for="(restData, rest_i) in props.trip.rest_data" :key="rest_i">
      {{ formatTimeFromSeconds(restData.total) }}
    </p>
  </td>
  <td :rowspan="rowSpan">{{ formattedOverTime }}</td>
  <td :rowspan="rowSpan">{{ formatTimeFromSeconds(overNightWorkTime) }}</td>
</template>

<style scoped>
td {
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 2px;
  padding-right: 2px;
  text-align: center;
  font-weight: bold !important;
  border: 1 !important;
  border-width: 2px !important;
}
p {
  line-height: 15px;
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 3px;
  margin-top: 3px;
}

.working-time-color-black {
  color: black;
}

.working-time-color-blue {
  color: blue;
}
</style>
